import React, { useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Card,
  MenuItem,
} from "@mui/material";
import { useInsertOrUpdateDataLoopingTableMutation } from "../../../Api/attoDeskApi";
import { useFormik } from "formik";
import CustomSwitch from "../../../Components/Switch/CustomSwitch";
import { useStyles } from "../../../theme/appStyles";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import { useNotifier } from "../../../Core/Notifier";

interface LoopingTableProps {
  variantName: string;
  productId: string;
  isLooping: boolean;
  loopingTableId: string;
  loopingTableComplete?: () => void;
  handleLoopingClose: (close: boolean) => void;
  loopingDetailsPopUp?: {
    tableName: string;
    shortTableName: string;
    tableColumns: {
      name: string;
      type: string;
      columnField: string;
      options: string[];
      value: any;
    }[];
  };
  customLoopingTableId?: string;
}

const LoopingTable: React.FC<LoopingTableProps> = ({
  variantName,
  productId,
  isLooping,
  loopingTableId,
  loopingTableComplete,
  loopingDetailsPopUp,
  customLoopingTableId,
  handleLoopingClose,
}) => {
  const classes = useStyles();
  const { showErrorMessage, showMessage } = useNotifier();
  const [updateDataLoopingTable, { isLoading: loopingDataLoading, isError }] =
    useInsertOrUpdateDataLoopingTableMutation();

  const LoopingTableList = useMemo(() => {
    return loopingDetailsPopUp?.tableColumns || [];
  }, [loopingDetailsPopUp]);

  const initialValues = useMemo(() => {
    const values: { [key: string]: any } = {};
    LoopingTableList.forEach((column) => {
      if (column.type === "boolean") {
        values[column.name] = Boolean(column.value);
      } else if (column.type === "integer") {
        values[column.name] = Number(column.value);
      } else {
        values[column.name] = column.value || "";
      }
    });
    return values;
  }, [LoopingTableList]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const temData = {
          id: customLoopingTableId,
          tableName: loopingDetailsPopUp?.shortTableName,
          data: values,
          productId: String(productId),
        };

        const addProductResponse = await updateDataLoopingTable(
          temData
        ).unwrap();
        if (!addProductResponse.status) {
          showErrorMessage(addProductResponse.message);
        } else {
          showMessage(addProductResponse.message);
          resetForm();
          if (loopingTableComplete) {
            loopingTableComplete();
          }
        }
      } catch (error) {
        showErrorMessage("Failed to insert data.");
      }
    },
  });
  const handleClose = () => {
    handleLoopingClose(false);
  };

  return (
    <Box>
      {loopingDataLoading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="10vh"
        >
          <CircularProgress />
        </Box>
      )}
      {isError && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="10vh"
        >
          <Typography>Error loading data...</Typography>
          <CircularProgress />
        </Box>
      )}

      {LoopingTableList.length > 0 && (
        <form onSubmit={formik.handleSubmit}>
          <Grid container>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Looping for {variantName}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container spacing={3} p={"24px"}>
                  <Grid item lg={12} md={12} sm={12} xs={12} py={2}>
                    {LoopingTableList.map((column) => {
                      switch (column.columnField) {
                        case "TextField":
                          return (
                            <Grid
                              container
                              direction="row"
                              alignItems="center"
                              spacing={2}
                              sx={{ mt: 2 }}
                              key={column.name}
                            >
                              <Grid item lg={2} md={3} sm={12} xs={12}>
                                <Typography
                                  variant="subtitle1"
                                >
                                  {column.name}
                                </Typography>
                              </Grid>
                              <Grid
                                item
                                lg={3}
                                md={9}
                                sm={12}
                                xs={12}
                                display="flex"
                                alignItems="center"
                              >
                                <TextField
                                  placeholder={column.name}
                                  size="small"
                                  type={
                                    column.type === "integer"
                                      ? "number"
                                      : "text"
                                  }
                                  value={formik.values[column.name]}
                                  onChange={formik.handleChange}
                                  name={column.name}
                                  className={classes.textField}
                                  InputProps={{
                                    sx: {
                                      fontSize: 14,
                                    },
                                  }}
                                  InputLabelProps={{
                                    sx: {
                                      fontSize: 14,
                                    },
                                  }}
                                />
                              </Grid>
                            </Grid>
                          );
                        case "Switch":
                          return (
                            <Grid
                              container
                              direction="row"
                              alignItems="center"
                              spacing={2}
                              sx={{ mt: 2 }}
                              key={column.name}
                            >
                              <Grid item lg={2} md={3} sm={12} xs={12}>
                                <Typography
                                  variant="subtitle1"
                                >
                                  {column.name}
                                </Typography>
                              </Grid>
                              <Grid
                                item
                                lg={3}
                                md={9}
                                sm={12}
                                xs={12}
                                display="flex"
                                alignItems="center"
                              >
                                <CustomSwitch
                                  color="primary"
                                  sx={{ mr: 2 }}
                                  formik={formik}
                                  name={column.name}
                                  onChange={formik.handleChange}
                                />
                              </Grid>
                            </Grid>
                          );
                        case "Selector":
                          return (
                            <Grid
                              container
                              direction="row"
                              alignItems="center"
                              spacing={2}
                              sx={{ mt: 2 }}
                              key={column.name}
                            >
                              <Grid item lg={2} md={3} sm={12} xs={12}>
                                <Typography
                                  variant="subtitle1"
                                >
                                  {column.name}
                                </Typography>
                              </Grid>
                              <Grid
                                item
                                lg={3}
                                md={9}
                                sm={12}
                                xs={12}
                                display="flex"
                                alignItems="center"
                              >
                                <TextField
                                  select
                                  size="small"
                                  className={classes.textField}
                                  value={formik.values[column.name]}
                                  onChange={formik.handleChange}
                                  name={column.name}
                                >
                                  <MenuItem
                                    value=""
                                    disabled
                                    style={{ color: "gray" }}
                                  >
                                    Select an option
                                  </MenuItem>
                                  {(column.options || []).map((option) => (
                                    <MenuItem key={option} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                            </Grid>
                          );
                        default:
                          return null;
                      }
                    })}
                  </Grid>
                </Grid>

                <Grid container spacing={3} p={"24px"}>
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<CloseIcon />}
                      className={classes.addButton}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                    <Box m={0.5}></Box>
                    <Button
                      variant="contained"
                      startIcon={
                        loopingDataLoading ? (
                          <CircularProgress size={18} className={classes.isLoading} />
                        ) : (
                          <SaveAltIcon />
                        )
                      }
                      className={classes.addButton}
                      onClick={() => formik.handleSubmit()}
                      // disabled={!formValid || isLoading || isImageUploading}
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </form>
      )}
    </Box>
  );
};

export default LoopingTable;
